import { Button, Input } from '../components/admin/ui';
import { useEffect, useState } from 'react';
import {
  Plus,
  Trash2,
  Pencil,
  X,
  Loader2,
  Zap,
  ToggleLeft,
  ToggleRight,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import {
  adminApi,
  AutomationRule,
  AutomationCondition,
  MacroAction,
  Profile,
} from '../lib/admin-api';

const triggerOptions = [
  { value: 'conversation.created', label: 'Conversation Created' },
  { value: 'conversation.updated', label: 'Conversation Updated' },
  { value: 'message.created', label: 'New Message' },
  { value: 'conversation.unattended', label: 'Conversation Unattended' },
];

const conditionFieldOptions = [
  { value: 'status', label: 'Status' },
  { value: 'priority', label: 'Priority' },
  { value: 'channel', label: 'Channel' },
  { value: 'assigned_agent_id', label: 'Assigned Agent' },
  { value: 'ai_handled', label: 'AI Handled' },
  { value: 'message.content', label: 'Message Content' },
  { value: 'tags', label: 'Tags' },
];

const operatorOptions = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

const actionTypeOptions = [
  { value: 'assign_agent', label: 'Assign to Agent' },
  { value: 'set_priority', label: 'Set Priority' },
  { value: 'set_status', label: 'Set Status' },
  { value: 'add_label', label: 'Add Label' },
  { value: 'remove_label', label: 'Remove Label' },
  { value: 'send_message', label: 'Send System Message' },
];

export function AutomationRulesPage() {
  const { profile } = useAuth();
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [agents, setAgents] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formTrigger, setFormTrigger] = useState('conversation.created');
  const [formConditions, setFormConditions] = useState<AutomationCondition[]>([]);
  const [formActions, setFormActions] = useState<MacroAction[]>([]);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    fetchData();
  }, [profile?.organization_id]);

  async function fetchData() {
    if (!profile?.organization_id) return;
    setLoading(true);
    const [rulesData, agentsData] = await Promise.all([
      adminApi.getAutomationRules(profile.organization_id),
      adminApi.getAgents(profile.organization_id),
    ]);
    setRules(rulesData);
    setAgents(agentsData);
    setLoading(false);
  }

  const resetForm = () => {
    setFormName('');
    setFormDescription('');
    setFormTrigger('conversation.created');
    setFormConditions([]);
    setFormActions([]);
    setFormError('');
    setShowForm(false);
    setEditingId(null);
  };

  const handleEdit = (rule: AutomationRule) => {
    setEditingId(rule.id);
    setFormName(rule.name);
    setFormDescription(rule.description || '');
    setFormTrigger(rule.trigger_event);
    setFormConditions(rule.conditions);
    setFormActions(rule.actions);
    setFormError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      setFormError('Name is required');
      return;
    }
    if (formActions.length === 0) {
      setFormError('At least one action is required');
      return;
    }

    setSaving(true);
    setFormError('');

    if (editingId) {
      const success = await adminApi.updateAutomationRule(editingId, {
        name: formName.trim(),
        description: formDescription.trim() || null,
        trigger_event: formTrigger,
        conditions: formConditions,
        actions: formActions,
      });
      if (!success) {
        setFormError('Failed to update rule');
        setSaving(false);
        return;
      }
    } else {
      if (!profile?.organization_id || !profile?.id) return;
      const result = await adminApi.createAutomationRule(
        profile.organization_id,
        profile.id,
        {
          name: formName.trim(),
          description: formDescription.trim() || null,
          is_active: true,
          trigger_event: formTrigger,
          conditions: formConditions,
          actions: formActions,
          execution_order: rules.length,
        }
      );
      if (!result) {
        setFormError('Failed to create rule');
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    resetForm();
    fetchData();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this automation rule?')) return;
    await adminApi.deleteAutomationRule(id);
    fetchData();
  };

  const handleToggle = async (rule: AutomationRule) => {
    await adminApi.updateAutomationRule(rule.id, { is_active: !rule.is_active });
    fetchData();
  };

  const addCondition = () => {
    setFormConditions([...formConditions, { field: 'status', operator: 'equals', value: '' }]);
  };

  const removeCondition = (index: number) => {
    setFormConditions(formConditions.filter((_, i) => i !== index));
  };

  const updateCondition = (index: number, updates: Partial<AutomationCondition>) => {
    setFormConditions(formConditions.map((c, i) => i === index ? { ...c, ...updates } : c));
  };

  const addAction = () => {
    setFormActions([...formActions, { type: 'set_priority', value: '' }]);
  };

  const removeAction = (index: number) => {
    setFormActions(formActions.filter((_, i) => i !== index));
  };

  const updateAction = (index: number, updates: Partial<MacroAction>) => {
    setFormActions(formActions.map((a, i) => i === index ? { ...a, ...updates } : a));
  };

  const getAgentName = (id: string) => {
    const agent = agents.find((a) => a.id === id);
    return agent?.display_name || agent?.email || id.slice(0, 8);
  };

  const describeTrigger = (event: string) =>
    triggerOptions.find((t) => t.value === event)?.label || event;

  return (
    <div className="support-page">
      <div className="support-page-heading">
        <div>
          <h1 className="support-page-title">Automations</h1>
          <p className="text-muted-foreground mt-1">
            Automate actions based on conversation events and conditions
          </p>
        </div>
        <Button variant="ghost"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          New Rule
        </Button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              {editingId ? 'Edit Rule' : 'New Automation Rule'}
            </h3>
            <Button aria-label="Close" size="iconSm" variant="ghost" onClick={resetForm} className="text-muted-foreground hover:text-muted-foreground">
              <X className="w-5 h-5" />
            </Button>
          </div>

          {formError && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-sm text-error-text">
              {formError}
            </div>
          )}

          <div className="space-y-4">
            {/* Name & Description */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="automationrulespage-1" className="block text-sm font-medium text-foreground mb-1">Name</label>
                <Input id="automationrulespage-1"
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Auto-assign urgent tickets"
                  className="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-primary"
                />
              </div>
              <div>
                <label htmlFor="automationrulespage-2" className="block text-sm font-medium text-foreground mb-1">Trigger</label>
                <select id="automationrulespage-2"
                  value={formTrigger}
                  onChange={(e) => setFormTrigger(e.target.value)}
                  className="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-primary"
                >
                  {triggerOptions.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Description <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <Input
                type="text"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Describe what this rule does..."
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:border-primary"
              />
            </div>

            {/* Conditions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-foreground">
                  Conditions <span className="text-muted-foreground font-normal">(all must match)</span>
                </label>
                <Button variant="ghost"
                  onClick={addCondition}
                  className="text-sm text-link hover:text-link font-medium"
                >
                  + Add condition
                </Button>
              </div>
              {formConditions.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">
                  No conditions — rule will fire for all matching events
                </p>
              ) : (
                <div className="space-y-2">
                  {formConditions.map((condition, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <select
                        value={condition.field}
                        onChange={(e) => updateCondition(i, { field: e.target.value })}
                        className="border border-border rounded-md px-2 py-1.5 text-sm"
                      >
                        {conditionFieldOptions.map((f) => (
                          <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                      </select>
                      <select
                        value={condition.operator}
                        onChange={(e) => updateCondition(i, { operator: e.target.value as AutomationCondition['operator'] })}
                        className="border border-border rounded-md px-2 py-1.5 text-sm"
                      >
                        {operatorOptions.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      {!['is_empty', 'is_not_empty'].includes(condition.operator) && (
                        <Input
                          type="text"
                          value={condition.value}
                          onChange={(e) => updateCondition(i, { value: e.target.value })}
                          placeholder="value"
                          className="flex-1 border border-border rounded-md px-2 py-1.5 text-sm"
                        />
                      )}
                      <Button aria-label="Remove condition" size="iconSm" variant="ghost" onClick={() => removeCondition(i)} className="text-muted-foreground hover:text-error-text">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-foreground">Actions</label>
                <Button variant="ghost"
                  onClick={addAction}
                  className="text-sm text-link hover:text-link font-medium"
                >
                  + Add action
                </Button>
              </div>
              {formActions.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">Add at least one action</p>
              ) : (
                <div className="space-y-2">
                  {formActions.map((action, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <select
                        value={action.type}
                        onChange={(e) => updateAction(i, { type: e.target.value as MacroAction['type'], value: '' })}
                        className="border border-border rounded-md px-2 py-1.5 text-sm"
                      >
                        {actionTypeOptions.map((a) => (
                          <option key={a.value} value={a.value}>{a.label}</option>
                        ))}
                      </select>
                      {action.type === 'assign_agent' ? (
                        <select
                          value={action.value}
                          onChange={(e) => updateAction(i, { value: e.target.value })}
                          className="flex-1 border border-border rounded-md px-2 py-1.5 text-sm"
                        >
                          <option value="">Select agent...</option>
                          {agents.map((a) => (
                            <option key={a.id} value={a.id}>{a.display_name || a.email}</option>
                          ))}
                        </select>
                      ) : action.type === 'set_priority' ? (
                        <select
                          value={action.value}
                          onChange={(e) => updateAction(i, { value: e.target.value })}
                          className="flex-1 border border-border rounded-md px-2 py-1.5 text-sm"
                        >
                          <option value="">Select priority...</option>
                          <option value="low">Low</option>
                          <option value="normal">Normal</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      ) : action.type === 'set_status' ? (
                        <select
                          value={action.value}
                          onChange={(e) => updateAction(i, { value: e.target.value })}
                          className="flex-1 border border-border rounded-md px-2 py-1.5 text-sm"
                        >
                          <option value="">Select status...</option>
                          <option value="open">Open</option>
                          <option value="pending">Pending</option>
                          <option value="resolved">Resolved</option>
                          <option value="closed">Closed</option>
                        </select>
                      ) : (
                        <Input
                          type="text"
                          value={action.value}
                          onChange={(e) => updateAction(i, { value: e.target.value })}
                          placeholder={action.type === 'send_message' ? 'Message text...' : 'value'}
                          className="flex-1 border border-border rounded-md px-2 py-1.5 text-sm"
                        />
                      )}
                      <Button aria-label="Remove action" size="iconSm" variant="ghost" onClick={() => removeAction(i)} className="text-muted-foreground hover:text-error-text">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <Button variant="ghost"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary disabled:bg-primary transition-colors text-sm font-medium flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {editingId ? 'Update' : 'Create'}
            </Button>
            <Button variant="ghost"
              onClick={resetForm}
              className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-border transition-colors text-sm font-medium"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Rules list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-link" />
        </div>
      ) : rules.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border border-border">
          <Zap className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No automation rules yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Create rules to automate repetitive tasks
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {rules.map((rule) => (
            <div
              key={rule.id}
              className={`bg-card rounded-lg border transition-colors ${
                rule.is_active ? 'border-border' : 'border-border opacity-60'
              }`}
            >
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Button variant="ghost"
                    onClick={() => handleToggle(rule)}
                    className="flex-shrink-0"
                    title={rule.is_active ? 'Disable rule' : 'Enable rule'}
                  >
                    {rule.is_active ? (
                      <ToggleRight className="w-6 h-6 text-success-foreground" />
                    ) : (
                      <ToggleLeft className="w-6 h-6 text-muted-foreground" />
                    )}
                  </Button>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{rule.name}</span>
                      <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-info text-info-foreground">
                        {describeTrigger(rule.trigger_event)}
                      </span>
                    </div>
                    {rule.description && (
                      <p className="text-sm text-muted-foreground truncate">{rule.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-4">
                  <Button aria-label="Toggle details" size="iconSm" variant="ghost"
                    onClick={() => setExpandedId(expandedId === rule.id ? null : rule.id)}
                    className="p-1.5 text-muted-foreground hover:text-muted-foreground rounded transition-colors"
                  >
                    {expandedId === rule.id ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </Button>
                  <Button aria-label="Edit item" size="iconSm" variant="ghost"
                    onClick={() => handleEdit(rule)}
                    className="p-1.5 text-muted-foreground hover:text-link hover:bg-primary/15 rounded transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button aria-label="Delete item" size="iconSm" variant="ghost"
                    onClick={() => handleDelete(rule.id)}
                    className="p-1.5 text-muted-foreground hover:text-error-text hover:bg-destructive/10 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Expanded details */}
              {expandedId === rule.id && (
                <div className="px-4 pb-4 pt-0 border-t border-border">
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Conditions</h4>
                      {rule.conditions.length === 0 ? (
                        <p className="text-sm text-muted-foreground italic">No conditions (fires always)</p>
                      ) : (
                        <ul className="space-y-1">
                          {rule.conditions.map((c, i) => (
                            <li key={i} className="text-sm text-muted-foreground">
                              <span className="font-mono text-xs bg-muted px-1 rounded">{c.field}</span>{' '}
                              {c.operator.replace('_', ' ')}{' '}
                              {c.value && <span className="font-medium">{c.value}</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Actions</h4>
                      <ul className="space-y-1">
                        {rule.actions.map((a, i) => (
                          <li key={i} className="text-sm text-muted-foreground">
                            <span className="capitalize">{a.type.replace('_', ' ')}</span>:{' '}
                            <span className="font-medium">
                              {a.type === 'assign_agent' ? getAgentName(a.value) : a.value}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
